import type { AxiosInstance } from 'axios';

export type BannerPlacement =
  | 'HOME_HERO'
  | 'HOME_STRIP'
  | 'CATEGORY_TOP'
  | 'HOME_SLIDER'
  | 'FLASH_DEALS'
  | 'TODAYS_DEAL'
  | 'BANNER_LEVEL_1'
  | 'BANNER_LEVEL_2'
  | 'BANNER_LEVEL_3'
  | 'CATEGORY_PRODUCTS'
  | 'CLASSIFIEDS'
  | 'TOP_BRANDS'
  | 'AUTH_ADMIN_LOGIN'
  | 'AUTH_CUSTOMER_LOGIN'
  | 'SPONSORED_SHOWCASE';

/** Where a live value came from. `mixed` means some fields from each source. */
export type ConfigSource = 'db' | 'env' | 'mixed' | 'none';

/**
 * A read-only picture of what each integration is actually using right now.
 * Identifying values only — sender ids, hosts, a key's visible prefix.
 */
export interface EffectiveConfig {
  sms: {
    provider: 'bizbrick' | 'smsindiahub' | 'console';
    configured: boolean;
    senderId: string | null;
    endpointIsHttps: boolean;
    source: ConfigSource;
    /** Set when a stored `console` selection is being ignored in production. */
    overruled: 'console-in-production' | null;
    dltTemplates: Array<{ purpose: string; label: string; hasOwnTemplate: boolean }>;
  };
  email: {
    configured: boolean;
    host: string | null;
    senderEmail: string | null;
    source: ConfigSource;
    quotedValue: boolean;
  };
  payments: {
    configured: boolean;
    source: ConfigSource;
    key: { mode: 'live' | 'test' | 'unknown'; hint: string } | null;
    webhookSecretSet: boolean;
  };
  shipping: {
    configured: boolean;
    account: string | null;
    source: ConfigSource;
    webhookTokenSet: boolean;
    quotedValue: boolean;
  };
  storage: { configured: boolean; cloudName: string | null; source: ConfigSource };
  maps: {
    provider: 'mapmyindia' | 'google';
    configured: boolean;
    /** The provider selected but not usable (no credentials), when that happened. */
    fellBack: 'mapmyindia' | 'google' | null;
    source: ConfigSource;
  };
  payouts?: { configured: boolean; source: ConfigSource; webhookSecretSet: boolean };
  gstVerification?: { provider: string; configured: boolean; source: ConfigSource };
  alerts?: { opsEmail: string | null };
  urls: { api: string; storefront: string; vendorPortal: string };
  appEnv: string;
}

/** What a stored secret reads as; sending it back unchanged keeps the stored value. */
export const SECRET_MASK = '••••••••';

export interface IntegrationTestResult {
  ok: boolean;
  message: string;
  mode?: 'live' | 'test' | 'unknown';
}

export interface CmsBanner {
  id: string;
  placement: BannerPlacement;
  imageFileId: string;
  imageUrl: string;
  // Optional alternates served at smaller viewports via <picture>/srcset.
  // Storefront falls back to `imageUrl` when these are null.
  tabletImageUrl: string | null;
  mobileImageUrl: string | null;
  linkUrl: string | null;
  alt: string | null;
  position: number;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
}

export interface BannerInput {
  placement: BannerPlacement;
  imageFileId?: string;
  imageUrl: string;
  tabletImageUrl?: string | null;
  mobileImageUrl?: string | null;
  linkUrl?: string | null;
  alt?: string | null;
  position?: number;
  active?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface WebsiteSetting {
  id: string;
  key: string;
  value: unknown;
  updatedAt: string;
  /**
   * Only on the public `shipping_settings` read: what the customer is charged
   * for delivery. Under QUOTED the zone table's free-delivery figures are not
   * an offer and must not be shown as one.
   */
  customerShippingPricing?: 'FLAT' | 'QUOTED';
}

export interface CmsPage {
  id: string;
  slug: string;
  title: string;
  contentHtml: string;
  published: boolean;
  updatedAt: string;
}

export interface CmsPageInput {
  slug: string;
  title: string;
  contentHtml: string;
  published?: boolean;
}

export function cmsEndpoints(http: AxiosInstance) {
  return {
    // storefront — active + scheduled banners only
    banners: (placement?: BannerPlacement) =>
      http
        .get<{ data: CmsBanner[] }>('/cms/banners', { params: placement ? { placement } : {} })
        .then((r) => r.data.data),

    // admin
    listBanners: (placement?: BannerPlacement) =>
      http
        .get<{
          data: CmsBanner[];
        }>('/admin/cms/banners', { params: placement ? { placement } : {} })
        .then((r) => r.data.data),

    createBanner: (body: BannerInput) =>
      http.post<CmsBanner>('/admin/cms/banners', body).then((r) => r.data),

    updateBanner: (bannerId: string, body: Partial<BannerInput>) =>
      http.patch<CmsBanner>(`/admin/cms/banners/${bannerId}`, body).then((r) => r.data),

    deleteBanner: (bannerId: string) =>
      http.delete<void>(`/admin/cms/banners/${bannerId}`).then((r) => r.data),

    // ── Website settings ──────────────────────────────────────────────────────

    /** Admin — list all website settings. */
    listSettings: () =>
      http.get<{ data: WebsiteSetting[] }>('/admin/website/settings').then((r) => r.data.data),

    /** Admin — get a single setting by key. */
    getSetting: (key: string) =>
      http
        .get<{ data: WebsiteSetting | null }>(`/admin/website/settings/${key}`)
        .then((r) => r.data.data),

    /** Storefront — public read of a single setting. */
    getPublicSetting: (key: string) =>
      http
        .get<{ data: WebsiteSetting | null }>(`/website/settings/${key}`)
        .then((r) => r.data.data),

    /**
     * Admin — what is actually live per integration, and whether it came from
     * the settings row or the server environment.
     *
     * The settings screens render the database row alone, so a platform
     * configured through the environment shows empty fields there. This is how
     * a screen can say what is really in effect instead of implying nothing is.
     * Identifying values only — no credential is returned.
     */
    getEffectiveConfig: () =>
      http.get<{ data: EffectiveConfig }>('/admin/settings/effective').then((r) => r.data.data),

    /** Admin — check that the credentials in use authenticate with the provider. */
    testIntegration: (name: 'razorpay' | 'shiprocket' | 'razorpayx' | 'google-maps') =>
      http
        .post<{ data: IntegrationTestResult }>(`/admin/settings/integrations/${name}/test`)
        .then((r) => r.data.data),

    /** Admin — create or update a website setting. */
    upsertSetting: (key: string, value: unknown) =>
      http.put<WebsiteSetting>(`/admin/website/settings/${key}`, { value }).then((r) => r.data),

    testSmtpSettings: (body: any) =>
      http
        .post<{ success: boolean }>('/admin/website/settings/smtp/test', body)
        .then((r) => r.data),

    // ── CMS Pages ─────────────────────────────────────────────────────────────

    /** Public — list published pages. */
    publicPages: () => http.get<{ data: CmsPage[] }>('/cms/pages').then((r) => r.data.data),

    /** Public — get a page by slug. */
    publicPageBySlug: (slug: string) => http.get<CmsPage>(`/cms/pages/${slug}`).then((r) => r.data),

    /** Admin — list all pages. */
    listPages: () => http.get<{ data: CmsPage[] }>('/admin/cms/pages').then((r) => r.data.data),

    /** Admin — get a page by ID. */
    getPage: (pageId: string) =>
      http.get<CmsPage>(`/admin/cms/pages/${pageId}`).then((r) => r.data),

    /** Admin — create a page. */
    createPage: (body: CmsPageInput) =>
      http.post<CmsPage>('/admin/cms/pages', body).then((r) => r.data),

    /** Admin — update a page. */
    updatePage: (pageId: string, body: Partial<CmsPageInput>) =>
      http.patch<CmsPage>(`/admin/cms/pages/${pageId}`, body).then((r) => r.data),

    /** Admin — delete a page. */
    deletePage: (pageId: string) =>
      http.delete<void>(`/admin/cms/pages/${pageId}`).then((r) => r.data),
  };
}

export type CmsEndpoints = ReturnType<typeof cmsEndpoints>;
